import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ComposableMap, Geographies, Geography, Annotation } from 'react-simple-maps';
import { scaleQuantize } from 'd3-scale';
import { geoCentroid } from 'd3-geo';
import { Helmet } from 'react-helmet';
import './Map.css';
import Footer from './Footer';
import CustomTooltip from './CustomTooltip';
import CostCalculator from './CostCalculator';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Define the color scale for dollars
const colorScaleDollar = scaleQuantize()
  .domain([2.4, 5.5])
  .range([
    "#006400", // $2.40 - $2.80
    "#228B22", // $2.81 - $3.20
    "#32CD32", // $3.21 - $3.60
    "#FFD700", // $3.61 - $4.00
    "#FF8C00", // $4.01 - $4.40
    "#FF4500", // $4.41 - $4.80
    "#DC143C", // $4.81 - $5.20
    "#8B0000"  // $5.21 - $5.50
  ]);

// Define the color scale for euros
const colorScaleEuro = scaleQuantize()
  .domain([2.4 * 0.264172 * 0.85, 5.5 * 0.264172 * 0.85]) // Adjust domain for euros
  .range([
    "#006400", // €0.54 - €0.63
    "#228B22", // €0.64 - €0.72
    "#32CD32", // €0.73 - €0.81
    "#FFD700", // €0.82 - €0.90
    "#FF8C00", // €0.91 - €0.99
    "#FF4500", // €1.00 - €1.08
    "#DC143C", // €1.09 - €1.17
    "#8B0000"  // €1.18 - €1.26
  ]);

const Legend = ({ unit }) => {
  const priceRanges = unit === "dollar_per_gallon" ? [
    { color: "#006400", label: "$2.40" },
    { color: "#228B22", label: "" },
    { color: "#32CD32", label: "" },
    { color: "#FFD700", label: "" },
    { color: "#FF8C00", label: "" },
    { color: "#FF4500", label: "" },
    { color: "#DC143C", label: "" },
    { color: "#8B0000", label: "$5.50" },
  ] : [
    { color: "#006400", label: "€0.54" },
    { color: "#228B22", label: "" },
    { color: "#32CD32", label: "" },
    { color: "#FFD700", label: "" },
    { color: "#FF8C00", label: "" },
    { color: "#FF4500", label: "" },
    { color: "#DC143C", label: "" },
    { color: "#8B0000", label: "€1.26" },
  ];

  return (
    <div className="legend-container">
      <div className="legend">
        {priceRanges.map((range, index) => (
          <div key={index} className="legend-block" style={{ backgroundColor: range.color }}>
            {range.label && <span className="legend-label">{range.label}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};


const MapChart = () => {
  const [fuelPrices, setFuelPrices] = useState({});
  const [selectedFuelType, setSelectedFuelType] = useState("gasoline");
  const [selectedUnit, setSelectedUnit] = useState("dollar_per_gallon");
  const [conversionRate, setConversionRate] = useState(0.85); // Default conversion rate
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const fetchData = async () => {
      // Local fuel data
      const localFuelData = {
        success: true,
        result: [
          {
              "currency": "usd",
              "name": "Alaska",
              "gasoline": "3.477",
              "midGrade": "3.638",
              "premium": "3.895",
              "diesel": "3.578"
          },
          {
              "currency": "usd",
              "name": "Alabama",
              "gasoline": "2.793",
              "midGrade": "3.206",
              "premium": "3.595",
              "diesel": "3.386"
          },
          {
              "currency": "usd",
              "name": "Arkansas",
              "gasoline": "2.827",
              "midGrade": "3.242",
              "premium": "3.613",
              "diesel": "3.268"
          },
          {
              "currency": "usd",
              "name": "Arizona",
              "gasoline": "3.409",
              "midGrade": "3.759",
              "premium": "4.071",
              "diesel": "3.576"
          },
          {
              "currency": "usd",
              "name": "California",
              "gasoline": "4.852",
              "midGrade": "5.045",
              "premium": "5.228",
              "diesel": "5.024"
          },
          {
              "currency": "usd",
              "name": "Colorado",
              "gasoline": "3.083",
              "midGrade": "3.479",
              "premium": "3.803",
              "diesel": "3.378"
          },
          {
              "currency": "usd",
              "name": "Connecticut",
              "gasoline": "3.050",
              "midGrade": "3.664",
              "premium": "4.005",
              "diesel": "3.828"
          },
          {
              "currency": "usd",
              "name": "District of Columbia",
              "gasoline": "3.229",
              "midGrade": "3.779",
              "premium": "4.184",
              "diesel": "3.792"
          },
          {
              "currency": "usd",
              "name": "Delaware",
              "gasoline": "3.076",
              "midGrade": "3.599",
              "premium": "3.881",
              "diesel": "3.644"
          },
          {
              "currency": "usd",
              "name": "Florida",
              "gasoline": "3.072",
              "midGrade": "3.505",
              "premium": "3.822",
              "diesel": "3.609"
          },
          {
              "currency": "usd",
              "name": "Georgia",
              "gasoline": "2.986",
              "midGrade": "3.443",
              "premium": "3.839",
              "diesel": "3.551"
          },
          {
              "currency": "usd",
              "name": "Hawaii",
              "gasoline": "4.525",
              "midGrade": "4.761",
              "premium": "5.007",
              "diesel": "5.289"
          },
          {
              "currency": "usd",
              "name": "Iowa",
              "gasoline": "3.100",
              "midGrade": "3.301",
              "premium": "3.847",
              "diesel": "3.430"
          },
          {
              "currency": "usd",
              "name": "Idaho",
              "gasoline": "3.366",
              "midGrade": "3.607",
              "premium": "3.852",
              "diesel": "3.560"
          },
          {
              "currency": "usd",
              "name": "Illinois",
              "gasoline": "3.487",
              "midGrade": "4.054",
              "premium": "4.505",
              "diesel": "3.595"
          },
          {
              "currency": "usd",
              "name": "Indiana",
              "gasoline": "3.284",
              "midGrade": "3.787",
              "premium": "4.289",
              "diesel": "3.648"
          },
          {
              "currency": "usd",
              "name": "Kansas",
              "gasoline": "2.841",
              "midGrade": "3.156",
              "premium": "3.448",
              "diesel": "3.277"
          },
          {
              "currency": "usd",
              "name": "Kentucky",
              "gasoline": "2.854",
              "midGrade": "3.387",
              "premium": "3.803",
              "diesel": "3.286"
          },
          {
              "currency": "usd",
              "name": "Louisiana",
              "gasoline": "2.759",
              "midGrade": "3.180",
              "premium": "3.562",
              "diesel": "3.249"
          },
          {
              "currency": "usd",
              "name": "Massachusetts",
              "gasoline": "2.973",
              "midGrade": "3.572",
              "premium": "3.930",
              "diesel": "3.826"
          },
          {
              "currency": "usd",
              "name": "Maryland",
              "gasoline": "3.202",
              "midGrade": "3.781",
              "premium": "4.099",
              "diesel": "3.725"
          },
          {
              "currency": "usd",
              "name": "Maine",
              "gasoline": "3.016",
              "midGrade": "3.531",
              "premium": "3.978",
              "diesel": "3.939"
          },
          {
              "currency": "usd",
              "name": "Michigan",
              "gasoline": "3.256",
              "midGrade": "3.751",
              "premium": "4.303",
              "diesel": "3.529"
          },
          {
              "currency": "usd",
              "name": "Minnesota",
              "gasoline": "3.183",
              "midGrade": "3.571",
              "premium": "4.003",
              "diesel": "3.455"
          },
          {
              "currency": "usd",
              "name": "Missouri",
              "gasoline": "2.918",
              "midGrade": "3.263",
              "premium": "3.572",
              "diesel": "3.248"
          },
          {
              "currency": "usd",
              "name": "Mississippi",
              "gasoline": "2.685",
              "midGrade": "3.123",
              "premium": "3.489",
              "diesel": "3.229"
          },
          {
              "currency": "usd",
              "name": "Montana",
              "gasoline": "3.150",
              "midGrade": "3.440",
              "premium": "3.754",
              "diesel": "3.405"
          },
          {
              "currency": "usd",
              "name": "North Carolina",
              "gasoline": "2.892",
              "midGrade": "3.349",
              "premium": "3.735",
              "diesel": "3.511"
          },
          {
              "currency": "usd",
              "name": "North Dakota",
              "gasoline": "3.100",
              "midGrade": "3.394",
              "premium": "3.747",
              "diesel": "3.449"
          },
          {
              "currency": "usd",
              "name": "Nebraska",
              "gasoline": "3.063",
              "midGrade": "3.240",
              "premium": "3.697",
              "diesel": "3.329"
          },
          {
              "currency": "usd",
              "name": "New Hampshire",
              "gasoline": "2.893",
              "midGrade": "3.468",
              "premium": "3.878",
              "diesel": "3.784"
          },
          {
              "currency": "usd",
              "name": "New Jersey",
              "gasoline": "2.970",
              "midGrade": "3.539",
              "premium": "3.781",
              "diesel": "3.656"
          },
          {
              "currency": "usd",
              "name": "New Mexico",
              "gasoline": "3.014",
              "midGrade": "3.428",
              "premium": "3.727",
              "diesel": "3.431"
          },
          {
              "currency": "usd",
              "name": "Nevada",
              "gasoline": "3.926",
              "midGrade": "4.218",
              "premium": "4.496",
              "diesel": "3.830"
          },
          {
              "currency": "usd",
              "name": "New York",
              "gasoline": "3.123",
              "midGrade": "3.622",
              "premium": "4.008",
              "diesel": "3.943"
          },
          {
              "currency": "usd",
              "name": "Ohio",
              "gasoline": "3.136",
              "midGrade": "3.663",
              "premium": "4.169",
              "diesel": "3.513"
          },
          {
              "currency": "usd",
              "name": "Oklahoma",
              "gasoline": "2.764",
              "midGrade": "3.142",
              "premium": "3.434",
              "diesel": "3.147"
          },
          {
              "currency": "usd",
              "name": "Oregon",
              "gasoline": "3.842",
              "midGrade": "4.046",
              "premium": "4.288",
              "diesel": "3.895"
          },
          {
              "currency": "usd",
              "name": "Pennsylvania",
              "gasoline": "3.303",
              "midGrade": "3.750",
              "premium": "4.109",
              "diesel": "4.047"
          },
          {
              "currency": "usd",
              "name": "Rhode Island",
              "gasoline": "2.966",
              "midGrade": "3.694",
              "premium": "4.028",
              "diesel": "3.789"
          },
          {
              "currency": "usd",
              "name": "South Carolina",
              "gasoline": "2.816",
              "midGrade": "3.256",
              "premium": "3.624",
              "diesel": "3.368"
          },
          {
              "currency": "usd",
              "name": "South Dakota",
              "gasoline": "3.124",
              "midGrade": "3.317",
              "premium": "3.769",
              "diesel": "3.356"
          },
          {
              "currency": "usd",
              "name": "Tennessee",
              "gasoline": "2.794",
              "midGrade": "3.217",
              "premium": "3.611",
              "diesel": "3.378"
          },
          {
              "currency": "usd",
              "name": "Texas",
              "gasoline": "2.771",
              "midGrade": "3.212",
              "premium": "3.566",
              "diesel": "3.179"
          },
          {
              "currency": "usd",
              "name": "Utah",
              "gasoline": "3.257",
              "midGrade": "3.501",
              "premium": "3.716",
              "diesel": "3.470"
          },
          {
              "currency": "usd",
              "name": "Virginia",
              "gasoline": "3.005",
              "midGrade": "3.485",
              "premium": "3.847",
              "diesel": "3.585"
          },
          {
              "currency": "usd",
              "name": "Vermont",
              "gasoline": "3.109",
              "midGrade": "3.665",
              "premium": "4.068",
              "diesel": "3.818"
          },
          {
              "currency": "usd",
              "name": "Washington",
              "gasoline": "4.213",
              "midGrade": "4.438",
              "premium": "4.664",
              "diesel": "4.453"
          },
          {
              "currency": "usd",
              "name": "Wisconsin",
              "gasoline": "3.136",
              "midGrade": "3.595",
              "premium": "4.156",
              "diesel": "3.332"
          },
          {
              "currency": "usd",
              "name": "West Virginia",
              "gasoline": "3.002",
              "midGrade": "3.444",
              "premium": "3.876",
              "diesel": "3.587"
          },
          {
              "currency": "usd",
              "name": "Wyoming",
              "gasoline": "3.159",
              "midGrade": "3.427",
              "premium": "3.705",
              "diesel": "3.437"
          }
      ],
      };

      // Uncomment the following code to fetch data from the API in the future
      /*
      const apiKey = "apikey 2csC6QIls55hJ4XysMwFJI:0G6i0SXSlWUHO4bRtSXHdI";
      const headers = {
        "content-type": "application/json",
        "authorization": `apikey ${apiKey}`,
      };

      try {
        const response = await axios.get("https://api.collectapi.com/gasPrice/allUsaPrice", { headers });
        if (response.data.success) {
          const prices = {};
          response.data.result.forEach((stateData) => {
            const state = stateData.name;
            prices[state] = {
              gasoline: stateData.gasoline ? parseFloat(stateData.gasoline) : null,
              midGrade: stateData.midGrade ? parseFloat(stateData.midGrade) : null,
              premium: stateData.premium ? parseFloat(stateData.premium) : null,
              diesel: stateData.diesel ? parseFloat(stateData.diesel) : null,
            };
          });
          setFuelPrices(prices);
        } else {
          console.error(`Error in response data: ${response.data.message}`);
        }
      } catch (error) {
        console.error(`Error fetching data:`, error);
      }
      */

      // Use local data instead of API response
      if (localFuelData.success) {
        const prices = {};
        localFuelData.result.forEach((stateData) => {
          const state = stateData.name;
          prices[state] = {
            gasoline: stateData.gasoline ? parseFloat(stateData.gasoline) : null,
            midGrade: stateData.midGrade ? parseFloat(stateData.midGrade) : null,
            premium: stateData.premium ? parseFloat(stateData.premium) : null,
            diesel: stateData.diesel ? parseFloat(stateData.diesel) : null,
          };
        });
        setFuelPrices(prices);
      } else {
        console.error("Error in local data");
      }
    };

    const fetchConversionRate = async () => {
      try {
        const response = await axios.get("https://api.exchangeratesapi.io/latest?base=USD&symbols=EUR");
        setConversionRate(response.data.rates.EUR);
      } catch (error) {
        console.error(`Error fetching conversion rate:`, error);
      }
    };

    fetchData();
    fetchConversionRate();
  }, []);

  const handleMouseLeave = () => {
    setHoveredState(null);
    setTooltipContent("");
  };

  const handleMouseMove = (event) => {
    setTooltipPosition({ x: event.pageX, y: event.pageY });
  };

  const convertPrice = (price) => {
    if (selectedUnit === "euro_per_liter") {
      return (price * 0.264172 * conversionRate).toFixed(2); // Convert dollars per gallon to euros per liter
    }
    return price.toFixed(2); // Default is dollars per gallon
  };

  const today = new Date().toLocaleDateString('en-US'); 

  return (
    <div className="container">
    <Helmet>
    <title>US Gas Price | Daily Gas Price Updates</title>
        <meta property="og:title" content="US Gas Price | Daily Gas Price Updates" />
        <meta property="og:description" content="Get the latest updates on gas prices across the US." />
        <meta property="og:image" content="https://example.com/data-map.png" />
        <meta property="og:url" content="https://usgasprice.com/map" />
        <meta property="og:type" content="website" />
    </Helmet>
      <header className="header">
        <h1>US Daily Gas Prices: {today}</h1>
      </header>
      <div className="map-container">
        <div className="map-content">
          <div className="button-container">
            <button
              className={`fuel-button ${selectedFuelType === "gasoline" ? "active" : ""}`}
              onClick={() => setSelectedFuelType("gasoline")}
            >
              Gasoline
            </button>
            <button
              className={`fuel-button ${selectedFuelType === "midGrade" ? "active" : ""}`}
              onClick={() => setSelectedFuelType("midGrade")}
            >
              MidGrade
            </button>
            <button
              className={`fuel-button ${selectedFuelType === "premium" ? "active" : ""}`}
              onClick={() => setSelectedFuelType("premium")}
            >
              Premium
            </button>
            <button
              className={`fuel-button ${selectedFuelType === "diesel" ? "active" : ""}`}
              onClick={() => setSelectedFuelType("diesel")}
            >
              Diesel
            </button>
          </div>
          <ComposableMap projection="geoAlbersUsa">
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const state = geo.properties.name;
                  const price = fuelPrices[state] ? fuelPrices[state][selectedFuelType] : null;
                  const convertedPrice = price ? convertPrice(price) : null;
                  const centroid = geoCentroid(geo);
                  const colorScale = selectedUnit === "dollar_per_gallon" ? colorScaleDollar : colorScaleEuro;
                  return (
                    <React.Fragment key={geo.rsmKey}>
                      <Geography
                        geography={geo}
                        fill={price ? colorScale(selectedUnit === "dollar_per_gallon" ? price : convertedPrice) : "#EEE"}
                        stroke={hoveredState === state ? "#FF0000" : "#FFF"}
                        strokeWidth={hoveredState === state ? 2 : 0.5}
                        style={{
                          default: {
                            outline: "none"
                          },
                          hover: {
                            outline: "none"
                          },
                          pressed: {
                            outline: "none"
                          }
                        }}
                        onMouseEnter={() => {
                          setHoveredState(state);
                          setTooltipContent(`${state}: ${convertedPrice ? `${selectedUnit === "euro_per_liter" ? "€" : "$"}${convertedPrice}` : 'N/A'}`);
                        }}
                        onMouseLeave={handleMouseLeave}
                        onMouseMove={handleMouseMove}
                      />
                      {convertedPrice && (
                        <Annotation
                          subject={centroid}
                          dx={0}
                          dy={0}
                          connectorProps={{
                            stroke: "none"
                          }}
                        >
                          <text x={0} y={0} textAnchor="middle" alignmentBaseline="middle" style={{ fontSize: 10, fontWeight:"bold", fill: "#FFF", textShadow: "1px 1px 3px #000" }}>
                            {`${selectedUnit === "euro_per_liter" ? "€" : "$"}${convertedPrice}`}
                          </text>
                        </Annotation>
                      )}
                    </React.Fragment>
                  );
                })
              }
            </Geographies>
          </ComposableMap>
          <CustomTooltip content={tooltipContent} position={tooltipPosition} />
          <div className="unit-tabs">
              <button
                className={`unit-tab ${selectedUnit === "dollar_per_gallon" ? "active" : ""}`}
                onClick={() => setSelectedUnit("dollar_per_gallon")}
              >
                $/gal
              </button>
              <button
                className={`unit-tab ${selectedUnit === "euro_per_liter" ? "active" : ""}`}
                onClick={() => setSelectedUnit("euro_per_liter")}
              >
                €/L
              </button>
            </div>
          <Legend unit={selectedUnit} />
          <CostCalculator /> 
        {/*  <StatePriceList fuelPrices={fuelPrices} selectedUnit={selectedUnit} conversionRate={conversionRate} />  */}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MapChart;